import React, { createContext, useState, useContext, useEffect } from 'react';
import * as authApi from '../services/api/authApi';
import * as adminApi from '../services/api/adminApi';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [impersonation, setImpersonation] = useState(null);

    useEffect(() => {
        // Check if user is logged in
        const token = localStorage.getItem('token');
        if (token) {
            fetchUser(token);
        } else {
            setLoading(false);
        }
        
        // Check for existing impersonation session
        const savedImpersonation = localStorage.getItem('impersonation');
        if (savedImpersonation) {
            try {
                setImpersonation(JSON.parse(savedImpersonation));
            } catch (error) {
                console.error('Error parsing saved impersonation:', error);
                localStorage.removeItem('impersonation');
            }
        }
    }, []);

    const fetchUser = async (token) => {
        try {
            const userData = await authApi.fetchCurrentUser();
            setUser(userData);
        } catch (error) {
            console.error('Error fetching user:', error);
            localStorage.removeItem('token');
        } finally {
            setLoading(false);
        }
    };

    const register = async (username, email, password) => {
        try {
            const data = await authApi.registerUser(username, email, password);
            return { success: true, message: data?.message };
        } catch (error) {
            return {
                success: false,
                error: error.data?.error || 'Failed to register'
            };
        }
    };

    const login = async (username, password) => {
        try {
            const data = await authApi.loginUser(username, password);
            const { token } = data;
            localStorage.setItem('token', token);
            await fetchUser(token);

            return { success: true };
        } catch (error) {
            const data = error.data;
            if (error.status === 403 && data?.code === 'EMAIL_NOT_VERIFIED') {
                return { success: false, error: data.error, code: data.code, email: data.email };
            }
            return {
                success: false,
                error: data?.error || 'Failed to login'
            };
        }
    };

    const verifyCode = async (email, code) => {
        try {
            const data = await authApi.verifyCode(email, code);
            const { token } = data;
            localStorage.setItem('token', token);
            await fetchUser(token);
            return { success: true };
        } catch (error) {
            const data = error.data;
            return {
                success: false,
                error: data?.error || 'Failed to verify code',
                code: data?.code,
                attemptsRemaining: data?.attemptsRemaining
            };
        }
    };

    const resendCode = async (email) => {
        try {
            const data = await authApi.resendCode(email);
            return { success: true, message: data?.message, code: data?.code };
        } catch (error) {
            const data = error.data;
            return {
                success: false,
                error: data?.error || 'Failed to resend code',
                retryAfterSeconds: data?.retryAfterSeconds
            };
        }
    };

    const forgotPassword = async (email) => {
        try {
            const data = await authApi.forgotPassword(email);
            return { success: true, message: data?.message };
        } catch (error) {
            const data = error.data;
            return {
                success: false,
                error: data?.error || 'Failed to send reset code',
                retryAfterSeconds: data?.retryAfterSeconds
            };
        }
    };

    const resetPassword = async (email, code, newPassword) => {
        try {
            const data = await authApi.resetPassword(email, code, newPassword);
            const { token } = data;
            localStorage.setItem('token', token);
            await fetchUser(token);
            return { success: true };
        } catch (error) {
            const data = error.data;
            return {
                success: false,
                error: data?.error || 'Failed to reset password',
                code: data?.code,
                attemptsRemaining: data?.attemptsRemaining
            };
        }
    };

    const updateUsername = async (username) => {
        try {
            const data = await authApi.updateUsername(username);
            const { token } = data;
            localStorage.setItem('token', token);
            await fetchUser(token);
            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: error.data?.error || 'Failed to update username'
            };
        }
    };

    const updatePassword = async (currentPassword, newPassword) => {
        try {
            const data = await authApi.updatePassword(currentPassword, newPassword);
            return { success: true, message: data?.message };
        } catch (error) {
            return {
                success: false,
                error: error.data?.error || 'Failed to update password'
            };
        }
    };

    const requestEmailChange = async (newEmail) => {
        try {
            const data = await authApi.requestEmailChange(newEmail);
            return { success: true, message: data?.message, pendingEmail: data?.pendingEmail, changeToken: data?.changeToken };
        } catch (error) {
            return {
                success: false,
                error: error.data?.error || 'Failed to request email change'
            };
        }
    };

    const confirmEmailChange = async (changeToken, code) => {
        try {
            const data = await authApi.confirmEmailChange(changeToken, code);
            setUser((prev) => (prev ? { ...prev, email: data.email } : prev));
            return { success: true, email: data.email };
        } catch (error) {
            return {
                success: false,
                error: error.data?.error || 'Failed to confirm email change'
            };
        }
    };

    const logout = () => {
        setUser(null);
        setImpersonation(null);
        localStorage.removeItem('token');
        localStorage.removeItem('impersonation');
    };

    const startImpersonation = async (targetUserId) => {
        try {
            const data = await adminApi.impersonateUser(targetUserId);

            const impersonationData = data.impersonation;
            setImpersonation(impersonationData);
            localStorage.setItem('impersonation', JSON.stringify(impersonationData));

            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: error.data?.error || 'Failed to start impersonation'
            };
        }
    };

    const stopImpersonation = async () => {
        try {
            await adminApi.stopImpersonation();

            setImpersonation(null);
            localStorage.removeItem('impersonation');

            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: error.data?.error || 'Failed to stop impersonation'
            };
        }
    };

    const value = {
        user,
        loading,
        impersonation,
        login,
        register,
        verifyCode,
        resendCode,
        forgotPassword,
        resetPassword,
        updateUsername,
        updatePassword,
        requestEmailChange,
        confirmEmailChange,
        logout,
        startImpersonation,
        stopImpersonation
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
} 